import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Globe, User, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { navigationLinks } from '../../data/mock';

const DONATION_URL = 'https://www.zeffy.com/en-US/donation-form/dc-men-in-early-childhood-education';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-navy-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/images/logo.png" 
              alt="DC Men in ECE Logo" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
            {navigationLinks.map((link) => (
              <div 
                key={link.name}
                className="relative"
                onMouseEnter={() => link.children && setOpenDropdown(link.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {link.children ? (
                  <>
                    <button
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap
                        ${link.children.some(c => isActive(c.path)) 
                          ? 'text-brand-green bg-green-50' 
                          : 'text-navy-700 hover:text-brand-green hover:bg-navy-50'}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === link.name && (
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-navy-100 py-2 min-w-48 animate-in fade-in slide-in-from-top-2 duration-200">
                        {link.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.path}
                            className={`block px-4 py-2.5 text-sm transition-colors duration-150 whitespace-nowrap
                              ${isActive(child.path) 
                                ? 'text-brand-green bg-green-50 font-medium' 
                                : 'text-navy-700 hover:text-brand-green hover:bg-navy-50'}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap
                      ${isActive(link.path) 
                        ? 'text-brand-green bg-green-50' 
                        : 'text-navy-700 hover:text-brand-green hover:bg-navy-50'}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button & Translate */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-navy-200 hover:border-brand-green transition-colors">
              <Globe className="w-4 h-4 text-navy-500" />
              <span className="text-xs font-medium text-navy-600 hidden xl:inline">Translate</span>
              <div id="google_translate_element" className="translate-widget"></div>
            </div>
            <Link to="/member-login">
              <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-navy-50 font-medium px-4 whitespace-nowrap">
                <User className="w-4 h-4 mr-2" />
                Member Login
              </Button>
            </Link>
            <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" data-testid="header-support-us-btn">
              <Button className="bg-brand-green hover:bg-green-700 text-white font-semibold px-5 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300 whitespace-nowrap">
                <Heart className="w-4 h-4 mr-2" />
                Support Us
              </Button>
            </a>
            <Link to="/join">
              <Button className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-6 shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40 transition-all duration-300 whitespace-nowrap">
                Join Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-navy-700">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <SheetTitle className="text-lg font-bold text-navy-900 mb-6">Menu</SheetTitle>
              <nav className="flex flex-col gap-2">
                {navigationLinks.map((link) => (
                  <div key={link.name}>
                    {link.children ? (
                      <div className="mb-2">
                        <span className="px-4 py-2 text-sm font-semibold text-navy-400 uppercase tracking-wider">
                          {link.name}
                        </span>
                        <div className="mt-2 ml-4 flex flex-col gap-1">
                          {link.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.path}
                              onClick={() => setIsOpen(false)}
                              className={`px-4 py-2.5 text-sm rounded-lg transition-colors duration-150
                                ${isActive(child.path) 
                                  ? 'text-brand-green bg-green-50 font-medium' 
                                  : 'text-navy-700 hover:text-brand-green hover:bg-navy-50'}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-150
                          ${isActive(link.path) 
                            ? 'text-brand-green bg-green-50' 
                            : 'text-navy-700 hover:text-brand-green hover:bg-navy-50'}`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Mobile Translate */}
                <div className="mt-4 pt-4 border-t border-navy-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-navy-500" />
                    <p className="text-sm text-navy-600 font-medium">Translate</p>
                  </div>
                  <div id="google_translate_element_mobile" className="translate-widget-mobile"></div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-navy-100 space-y-3">
                  <Link to="/member-login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full border-brand-navy text-brand-navy hover:bg-navy-50 font-medium">
                      <User className="w-4 h-4 mr-2" />
                      Member Login
                    </Button>
                  </Link>
                  <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-support-us-btn" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-brand-green hover:bg-green-700 text-white font-semibold shadow-lg">
                      <Heart className="w-4 h-4 mr-2" />
                      Support Us
                    </Button>
                  </a>
                  <Link to="/join" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold shadow-lg">
                      Join Us
                    </Button>
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
